// case-studies/pages/deloitte250k.js
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import "../styles/deloitte250k.css";

import example1 from "../assets/deloitte250/250header.png";
import example2 from "../assets/deloitte250/webcrawler.jpg";
import example3 from "../assets/deloitte250/webscraper.png";
import example4 from "../assets/deloitte250/laptopinbag.jpg";
import example5 from "../assets/deloitte250/taughtme.jpg";

const titleVariant = {
  hidden: { opacity: 0, y: -28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const textVariant = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const imageVariant = {
  hidden: { opacity: 0, scale: 1.18 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const Deloitte250k = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, []);

  return (
    <main className="dd-root">
      {/* HEADER */}
      <header className="dd-header">
        <div className="dd-header-inner">
          <div className="dd-title-block">
            <motion.h1
              className="dd-title"
              variants={titleVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.6 }}
            >
              DELOITTE: THE $250,000 DEAL
            </motion.h1>

            <motion.p
              className="dd-subtitle"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.6 }}
            >
              How a junior, a broken sitemap, and an overworked laptop almost
              closed a quarter million dollar GenAI deal.
            </motion.p>
          </div>

          <div className="dd-meta-row">
            <motion.div
              className="dd-meta-line"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            />
            <motion.div
              className="dd-meta-items"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <div className="dd-meta-item">
                <span className="dd-meta-label">Project Type</span>
                <span className="dd-meta-value">
                  GenAI search and RAG prototype
                </span>
              </div>
              <div className="dd-meta-item">
                <span className="dd-meta-label">Role</span>
                <span className="dd-meta-value">
                  Lead prototype developer and demo owner
                </span>
              </div>
              <div className="dd-meta-item">
                <span className="dd-meta-label">Year</span>
                <span className="dd-meta-value">2023</span>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* BANNER */}
      <section className="dd-banner">
        <div className="dd-banner-inner">
          <motion.img
            src={example1}
            alt="Generative search concept for a tourism platform"
            className="dd-banner-image"
            variants={imageVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          />
        </div>
      </section>

      {/* 1. HOOK */}
      <section className="dd-section dd-section-full">
        <div className="dd-section-inner">
          <motion.h2
            className="dd-section-title dd-section-title-center"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            The Hook
          </motion.h2>

          <motion.div
            className="dd-section-body dd-body-narrow"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <p>
              After the soon to be award winning Tax Genie that I helped
              bootstrap and build actually started working,{" "}
              <span className="dd-emphasis-inline">
                another idea landed on my desk
              </span>
              . This one came from the Abu Dhabi office.{" "}
              <span className="dd-emphasis-inline">
                A sovereign tourism authority from a Gulf country and Deloitte
                had been collaborating
              </span>{" "}
              heavily for years. They even{" "}
              <span className="dd-emphasis-inline">
                handled the search engine for that country's main tourism
                portal
              </span>
              . And if you have ever used that search bar, let me tell you
              something important.
            </p>
            <p>
              <span className="dd-emphasis-inline">It was awful.</span> You
              typed "hiking" and it would proudly show you "Chuck E Cheese".
            </p>
            <p>
              So naturally an idea appeared. “Why do we not build a generative
              search engine for their tourism platform?” This was the middle of
              2023.{" "}
              <span className="dd-emphasis-inline">
                GenAI adoption was basically: "ChatGPT exists. Good luck."
              </span>{" "}
              Nobody in the region had launched anything serious with GenAI yet.
            </p>
            <p>
              Perfect timing. Perfect opportunity. Perfect disaster waiting to
              happen.
            </p>
            <p>
              At this point in Deloitte, I had become the unofficial tech
              handyman. Azure issue? John can fix it. Linux VM? John can set it
              up. Need a server with ports open and a few dark magic rituals?
              Bring it to John. So when this idea came up, my manager Kunal grabbed
              me to brainstorm. And in classic consultant fashion, after one
              week of bouncing ideas, the conclusion was: “RAG. Obviously.”
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. CONTEXT (title, image, text in portrait) */}
      <section className="dd-section dd-section-split dd-split-text-left">
        <div className="dd-section-inner dd-section-inner-split">
          <motion.h3
            className="dd-section-title dd-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            Context
          </motion.h3>

          <div className="dd-split-col dd-split-col-media">
            <div className="dd-image-frame">
              <motion.img
                src={example2}
                alt="Tourism platform sitemap context"
                className="dd-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="dd-split-col dd-split-col-text">
            <motion.div
              className="dd-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>
                To build a generative search engine for their tourism platform,
                we needed all the data from all the pages on the site. Except{" "}
                <span className="dd-emphasis-inline">
                  the sitemap they gave us was outdated. Unusable. Wrong.
                </span>{" "}
                Half the website was not even on it.
              </p>
              <p>
                So now it was not a GenAI problem. It was a “the website does
                not even know what the website is” problem. I had two options.
                Cry. Or{" "}
                <span className="dd-emphasis-inline">
                  build my own web crawler
                </span>
                . So naturally, I built my own web crawler.
              </p>
              <p>
                It hunted down every page on the platform like a bloodhound. I
                took the results back to the project owner, showed them the
                actual structure of their own platform, and gently suggested
                that we update the sitemap before building a GenAI product on
                top of it.
              </p>
              <p>
                One existential crisis later,{" "}
                <span className="dd-emphasis-inline">
                  we had all the real pages
                </span>
                .
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. CHALLENGE (title, image, text in portrait) */}
      <section className="dd-section dd-section-split dd-split-text-right">
        <div className="dd-section-inner dd-section-inner-split">
          <motion.h3
            className="dd-section-title dd-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            The Challenge
          </motion.h3>

          <div className="dd-split-col dd-split-col-media">
            <div className="dd-image-frame">
              <motion.img
                src={example3}
                alt="Scraping and RAG challenge"
                className="dd-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="dd-split-col dd-split-col-text">
            <motion.div
              className="dd-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>Scraping them was the next mission.</p>
              <p>
                <span className="dd-emphasis-inline">
                  Anyone who has done manual web scraping knows this pain.
                </span>{" "}
                Dynamic classes. Random markup. Frontend chaos. Selenium acting
                like it has personal beef with you. Me, BeautifulSoup4, Selenium
                and a lot of coffee entered a toxic relationship for about a
                week. Around ninety hours later, every single page was scraped,
                cleaned, and chunked.
              </p>
              <p>
                And then the hallucinations started. The model kept inventing
                facts like it was trying to impress someone. Early days of GenAI
                meant every technique was “state of the art” which usually
                translates to “held together by hopes, prayers, and two GitHub
                issues.”
              </p>
              <p>That was when I met Beslan Atabaev.</p>
              <p>
                The Tony Stark of Deloitte Middle East. One of the smartest
                people I have ever met. Also a Jiu Jitsu menace. He suggested
                using a{" "}
                <span className="dd-emphasis-inline">
                  smart chunking RAG pipeline
                </span>{" "}
                his team had built for another Deloitte AI Institute project. We
                applied it. The hallucinations dropped. Suddenly the system
                behaved like it had a master’s degree. Two weeks in, we had a
                shockingly solid proof of concept.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. STRATEGY (full width) */}
      <section className="dd-section dd-section-full">
        <div className="dd-section-inner">
          <motion.h2
            className="dd-section-title dd-section-title-center"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Strategy
          </motion.h2>

          <motion.div
            className="dd-section-body dd-body-narrow"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <p>
              Now came the pitch. My manager Kunal Kohli built a killer deck. The POC
              worked. The idea made sense. Everything was lining up. Except for
              one tiny problem.
            </p>
            <p>
              <span className="dd-emphasis-inline">
                Someone had to demo it.
              </span>
            </p>
            <p>
              And unfortunately, I was the only person who actually understood
              every moving part of the prototype. But{" "}
              <span className="dd-emphasis-inline">
                juniors tyically do not demo to clients
              </span>
              . It is a whole hierarchy thing. Except this was not a normal
              demo.{" "}
              <span className="dd-emphasis-inline">
                This was a quarter million dollar pitch.
              </span>
            </p>
            <p>
              So Kunal did the deck. And I walked them through the demo.
              Director on the tourism authority side. Partner, Manager, Senior
              Consultant and… <span className="dd-emphasis-inline">me</span>. Mr
              Junior himself.{" "}
              <span className="dd-emphasis-inline">
                The kid who was not supposed to be in the room.
              </span>{" "}
              The demo went surprisingly well. The client representative from
              the tourism authority liked what he saw. We promised a more
              refined solution in the next iteration.{" "}
              <span className="dd-emphasis-inline">
                And just like that, the 250,000 dollar deal was becoming real.
              </span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* 5. OUTCOME – title, image, text layout */}
      <section className="dd-section dd-section-split dd-split-text-left">
        <div className="dd-section-inner dd-section-inner-split">
          <motion.h2
            className="dd-section-title dd-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Outcome
          </motion.h2>

          <div className="dd-split-col dd-split-col-media">
            <div className="dd-image-frame">
              <motion.img
                src={example5}
                alt="Outcome visual"
                className="dd-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="dd-split-col dd-split-col-text">
            <motion.div
              className="dd-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
            >
              <p>
                Right as things were heating up, my Deloitte contract was
                ending. They were not in a hiring period yet. The AI and Data
                team wanted me to stay, but I needed a break from consulting.
              </p>
              <p>
                So I jumped into Web3. A completely different world. A
                completely different learning curve.
              </p>
              <p>
                But this Deloitte project stayed with me. A web crawler I built
                from scratch. Hundreds of scraped pages no one else could get.
                Weeks of RAG experimentation. A GenAI system that barely had
                literature supporting it. A pitch I was not supposed to be part
                of. And a 250,000 dollar deal born from it.
              </p>
              <p>
                Even though I stepped away before the next stage,{" "}
                <span className="dd-emphasis-inline">
                  I know my fingerprints were all over that project
                </span>
                .
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 6. REFLECTION – title, image, text layout */}
      <section className="dd-section dd-section-split dd-split-text-right dd-section-reflection">
        <div className="dd-section-inner dd-section-inner-split">
          <motion.h2
            className="dd-section-title dd-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Reflection
          </motion.h2>

          <div className="dd-split-col dd-split-col-media">
            <div className="dd-image-frame dd-image-frame-soft">
              <motion.img
                src={example4}
                alt="Reflection visual"
                className="dd-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="dd-split-col dd-split-col-text">
            <motion.div
              className="dd-section-body dd-body-narrow"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
            >
              <p>This project taught me three things.</p>
              <p>
                One. Most problems look impossible until someone sits down and
                actually builds the painful part.
              </p>
              <p>
                Two. Titles do not matter as much as competence. If you are the
                only one who understands the system, you are the one people
                trust in the room.
              </p>
              <p>
                Three. The coolest opportunities show up when you are too busy
                trying to fix something no one else wants to touch.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Deloitte250k;
